import React from "react";
import { 
  Box, 
  HStack, 
  Tag, 
  TagLabel, 
  Divider, 
  Text, 
  IconButton, 
  Tooltip, 
  MenuList, 
  Menu, 
  MenuItem, 
  MenuButton, 
  Icon 
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";
import { ColumnType } from "build-ts/enums";
import { FilterProto, MetricProto } from "build-ts/metadata";
import DataTableMKG from "components/analyse/data_table/DataTableMKG";
import { NodeInsights } from "containers/Interfaces";
import { useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import { FaChevronDown, FaDownload, FaFilter, FaTimes } from "react-icons/fa";
import { IoCopyOutline } from "react-icons/io5";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getDateFilterName } from "utils/nameUtils";
import { buildActualsMap, copyTableToClipboard, downloadTableAsExcel, getNonMeasureData, isColumnMissing } from "components/analyse/data_table/dataTableUtils";
import { BsFillInfoSquareFill } from "react-icons/bs";
import { useUser } from "context/UserContext";

interface DataTableComponentProps {
  observationData: NodeInsights;
  metrics?: Map<string, MetricProto>;

  // close function for table
  onCloseTable?: () => void;
}

const DataTableComponent: React.FC<DataTableComponentProps> = ({ 
  observationData,
  metrics, 
  onCloseTable,
}) => { 
  const { configurator, setObservationData, errorDetails } = useAnalysisPlanContext();
  const { defaultTimeZone, quarterStartMonth } = useUser();

  const actualsMap =  buildActualsMap(observationData.current_node?.kg_observation_data!);
  const { nonMeasureColumns } = getNonMeasureData(observationData, defaultTimeZone, quarterStartMonth);

  let noOfRows = 0;
  if(nonMeasureColumns.length === 0) {
    noOfRows = Object.keys(actualsMap).length;
  }
  else {
    noOfRows = actualsMap[Object.keys(actualsMap)[0]]?.actual_value?.length || 0;
  }

  // Function to close the table
  const handleCloseTable = () => {
    if (onCloseTable) {
      // Use the custom close function if provided (e.g., from ChatPage)
      onCloseTable();
    } else {
      // Default behavior for AnalysisPlan
      setObservationData(null);
      configurator.onClose();
    }
  };

  // function to render the filters
  const renderFilters = () => {

    let filters: FilterProto[] = [];
  
    if (observationData.current_node?.observation?.content) {
      filters = observationData.current_node.observation.content.filters || [];
    } 
    // If not, check if node.current_node.kg_observation is present
    else if (observationData.current_node?.kg_observation) {
      filters = observationData.current_node.kg_observation.filters || [];
    }

    if(filters?.length === 0 || !filters) return null;

    const groupedFilters = filters.reduce((acc, filter) => {
      let colunName = "";
      if(filter?.logical_column_header?.name) {
        colunName = filter?.logical_column_header?.name;
      } else if (filter?.kg_column_header?.name) {
        colunName = filter?.kg_column_header?.name;
      }
      let key = "";
      if(filter?.logical_column_header?.column_type === ColumnType.TEMPORAL) {
        key = "DATE_FILTERS";
      } else {
        key = colunName;
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(filter);
      return acc;
    }, {} as Record<string, FilterProto[]>);
    
  
    return (
      <Box
        overflowX="auto"
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        <HStack spacing={1} py={1}>
          {Object.entries(groupedFilters).map(([key, filtersGroup], index) => {
            // Special handling for date filters
            if (key === "DATE_FILTERS") {
              return (
                <Tag
                  key={index}
                  size="sm"
                  borderRadius="md"
                  variant="solid"
                  bg="gray.50"
                  color="gray.800"
                  borderWidth="1px"
                  borderColor="gray.300"
                  p={1}
                  minW="fit-content"
                >
                  <TagLabel 
                    fontSize="sm"
                    textAlign="left"
                    whiteSpace="normal"
                  >
                    <Box as="span" fontWeight="700">
                      Date
                    </Box>
                    <Box as="span" fontWeight="normal">
                      {filtersGroup.map((filter, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && " | "}
                          {" "}
                          {getOperatorSymbol(filter?.operator!)}
                          {" "}
                          {filter?.values?.length > 0 
                            ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                            : filter?.date_filter?.absolute_date_filter ? getDateFilterName(filter?.date_filter?.absolute_date_filter!) : getDateFilterName(filter?.date_filter!)}
                        </React.Fragment>
                      ))}
                    </Box>
                  </TagLabel> 
                </Tag>
              );
            }
            
            // Regular handling for non-date filters
            return filtersGroup.map((filter, i) => (
              <Tag
                key={`${index}-${i}`}
                size="sm"
                borderRadius="md"
                variant="solid"
                bg="gray.50"
                color="gray.800"
                borderWidth="1px"
                borderColor="gray.300"
                p={1}
                minW="fit-content"
              >
                <TagLabel 
                  fontSize="sm"
                  textAlign="left"
                  whiteSpace="normal"
                >
                  <Box as="span" fontWeight="700">
                    {filter?.kg_column_header?.name}
                  </Box>
                  <Box as="span" fontWeight="normal">
                    {" "}
                    {getOperatorSymbol(filter?.operator!)}
                    {" "}
                    {filter?.values?.length > 0 
                      ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                      : filter?.date_filter?.absolute_date_filter ? getDateFilterName(filter?.date_filter?.absolute_date_filter!) : getDateFilterName(filter?.date_filter!)}
                  </Box>
                </TagLabel> 
              </Tag>
            ));
          })}
        </HStack>
      </Box>
    );
  };
  
  return (
    <Box
      bg="transparent"
      position="relative"
      overflow="hidden"
      p={0}
      width="100%"
      height="auto"
      borderWidth={1}  // Add a highlighted border when this node is selected
      borderColor="purple.700"
      borderRadius="md"
      transition="all 0.2s ease-in-out"
    >
      <HStack 
        justify="space-between" 
        align="center" 
        w="100%" 
        p={0}
        borderBottomWidth={1}
        borderColor="gray.200"
        spacing={0}
        bg="transparent"
        _hover={{ bg: "gray.100", cursor: "pointer" }}
      >
        <HStack spacing={1} pl={1} flex={1} minWidth={0}>
          <Box flex={1} minWidth={0}>
            <Box
              overflowX="auto"
              css={{
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E0 transparent",
                "&::-webkit-scrollbar": {
                  height: "3px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E0",
                  borderRadius: "3px",
                },
              }}
            >
              <HStack spacing={2}>
                <Tag
                  size="sm"
                  borderRadius="md"
                  variant="solid"
                  bg="gray.50"
                  color="gray.800"
                  borderWidth="1px"
                  borderColor="gray.300"
                  p={1}
                >
                  <HStack spacing={1}>
                    <Icon as={FaFilter} boxSize={3} color="purple.700" />
                  </HStack>
                </Tag>
                <Divider orientation="vertical" height="2vh" my={0} borderColor="gray.400" />
                {renderFilters()}
              </HStack>
            </Box>
          </Box>
        </HStack>
        <HStack spacing={0} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          {noOfRows && noOfRows > 0 && (
            <Tooltip
              label={`${noOfRows} rows present`}
              placement="left"
              hasArrow
              bg="gray.700"
              color="white"
              fontSize="sm"
              p={2}
              borderRadius="md"
              maxWidth="200px"
            >
              <IconButton
                size="xl"
                variant="ghost"
                colorScheme="gray"
                aria-label="No of rows present"
                icon={< Icon as={BsFillInfoSquareFill} boxSize={5} color="gray.500" />}
                _hover={{ bg: "gray.100" }}
              />
            </Tooltip>
          )}
        {/* Error during node data fetch */}
        {errorDetails && errorDetails.length > 0 && (
          <Tooltip
            label={
              <Box>
                <Text fontWeight="semibold" mb={1}>Errors Details:</Text>
                <ul style={{ paddingLeft: "16px" }}>
                  {errorDetails.map((error, index) => (
                    <li key={index}>{error.error_msg}</li>
                  ))}
                </ul>
              </Box>
            }
            placement="left"
            hasArrow
            bg="red.500"
            color="white"
            fontSize="sm"
            p={2}
            borderRadius="md"
            maxWidth="300px"
          >
            <IconButton
              size="xl"
              variant="ghost"
              colorScheme="red"
              aria-label="View errors"
              icon={<WarningTwoIcon boxSize={5} />}
              _hover={{ bg: "red.100" }}
            />
          </Tooltip>
        )}

        {/* Missing column warning Icon Button */}
        {observationData?.current_node?.kg_observation_data?.missing_columns && observationData?.current_node?.kg_observation_data?.missing_columns.length > 0 && (
            <Tooltip
              label={
                <Box whiteSpace="pre-line">
                  {isColumnMissing(observationData, observationData?.current_node?.kg_observation_data!)}
                </Box>
              }
              placement="top"
              hasArrow
            >
              <IconButton 
                aria-label="Filters"
                icon={<WarningTwoIcon boxSize="20px" color="yellow.500" opacity={0.7} />}
                size="xs"
                variant="ghost"
                minW="20px"
                height="20px"
                pr={2}
                p={0}
              />
            </Tooltip>
          )}

        {/* Copy Icon Button and Download Icon Button*/}
        <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
          <MenuButton
            as={IconButton}
            size="xs"
            color="gray.500"
            bg="transparent"
            _hover={{ bg: "gray.50", color: "gray.800" }}
            aria-label="Table options"
            icon={
              <HStack spacing={0}>
                <Icon as={IoCopyOutline} boxSize={5} />
                <Icon as={FaChevronDown} boxSize={3} ml={0.5} />
              </HStack>
            }
            onClick={(e) => e.stopPropagation()}
            mr={1}
            title="Table options"
          />
          <MenuList 
            minW="100px" 
            zIndex={1000} 
            shadow="lg" 
            border="1px solid" 
            borderColor="gray.200"
            p={1}
            position="relative"
          >
            <MenuItem 
              icon={<Icon as={IoCopyOutline} boxSize={4} />}
              onClick={(e) => {
                e.stopPropagation();
                copyTableToClipboard();
              }}
            fontSize="sm"
            >
              Copy to clipboard
            </MenuItem>
            <MenuItem 
              icon={<Icon as={FaDownload} boxSize={4} />}
              onClick={(e) => {
                e.stopPropagation();
                downloadTableAsExcel(observationData);
              }}
              fontSize="sm"
            >
              Download as Excel
            </MenuItem>
          </MenuList>
        </Menu>
        {/* Close Icon Button */}
        <IconButton
          icon={<Icon as={FaTimes} boxSize={4} />}
          onClick={() => handleCloseTable()}
          aria-label="Close"
          size="xs"
          color="red.400"
          colorScheme="gray"
          variant="ghost"
        />
        </HStack>
      </HStack>
      {/* Data Table */}
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
        p={0}
        width="100%"
        maxHeight="330px" // Match the TableContainer maxHeight
        height="auto"
        opacity={1}
        transition="all 0.3s ease-in-out"
        visibility="visible" // Add visibility transition
        mb={1}
      >
        {observationData && observationData.current_node.kg_observation_data && (
          <Box 
            flex="1" 
            overflow="auto"
            maxHeight="calc(50vh - 100px)"
            onWheel={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              target.scrollTop += e.deltaY;
            }}
          >
            <DataTableMKG 
              node={observationData}
              isChatOpen={false}
              metrics={metrics}
              treePannelLoader={false}
              showEditSuggestions={false}
              isOverlay={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DataTableComponent;